import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from "@mui/material";
import type { AllowedIpPrefixDto } from "client/api/CloudSubscriptionApi";
import { updateCloudSubscriptionAllowedIpAddress } from "client/api/CloudSubscriptionApi";
import { SubmittableForm } from "components/form/SubmittableForm";

type CloudSubscriptionUpdateAllowedIpAddressDialogProps = {
  cloudSubscriptionId: string;
  allowListActive: boolean;
  existingIpAddress?: AllowedIpPrefixDto;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  open: boolean;
};

type CloudSubscriptionUpdateAllowedIpAddressDialogSubmitArgs = {
  ipAddress: string;
  description: string;
};

const ipAddressOptions = {
  required: {
    value: true,
    message: "Please enter an IP address or range",
  },
  validate: (value: string) => {
    const trimmedValue = value.trim();
    if (isValidIPv4(trimmedValue) || isValidIPv4Cidr(trimmedValue)) {
      return true;
    }
    return "Please enter a valid IPv4 address (e.g. 203.0.113.1) or IPv4 CIDR prefix (e.g. 203.0.113.0/24)";
  },
};

export function CloudSubscriptionUpdateAllowedIpAddressDialog(
  props: CloudSubscriptionUpdateAllowedIpAddressDialogProps
) {
  const { cloudSubscriptionId, allowListActive, existingIpAddress, onClose, onConfirm, open } = props;

  const handleSubmit = async (args: CloudSubscriptionUpdateAllowedIpAddressDialogSubmitArgs) => {
    await updateCloudSubscriptionAllowedIpAddress(
      cloudSubscriptionId,
      existingIpAddress?.prefix,
      args.ipAddress,
      args.description
    );
    await onConfirm();
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>
        {existingIpAddress ? "Edit" : "Add"} an IP address or range
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} rowSpacing={2} sx={{ pt: 2 }}>
          <Grid size={12}>
            <SubmittableForm
              submitAction={handleSubmit}
              onCancel={onClose}
              mode="onBlur"
              defaultValues={{
                ipAddress: existingIpAddress?.prefix ?? "",
                description: existingIpAddress?.description ?? "",
              }}
            >
              {(formController) => {
                const { register, formState } = formController;
                return (
                  <>
                    <Grid size={12}>
                      <TextField
                        id={"ip-address"}
                        autoFocus
                        required
                        fullWidth
                        label={"IP address or range"}
                        placeholder="127.0.0.1/32"
                        error={!!formState?.errors?.ipAddress}
                        helperText={formState?.errors?.ipAddress?.message}
                        {...register("ipAddress", ipAddressOptions)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={"description"}
                        fullWidth
                        label={"Description"}
                        placeholder="Head Office"
                        error={!!formState?.errors?.description}
                        helperText={formState?.errors?.description?.message}
                        {...register("description")}
                      />
                    </Grid>
                    {!existingIpAddress && (
                      <Grid size={12}>
                        <Typography>
                          Note: Adding an IP address or range that already exists in the allow list will update the
                          Description.
                        </Typography>
                      </Grid>
                    )}
                    {allowListActive && (
                      <Grid size={12}>
                        <Typography
                          sx={{
                            marginBottom: "16px",
                          }}
                        >
                          Changes to IP address allow lists can take up to 60 seconds to take effect.
                        </Typography>
                      </Grid>
                    )}
                  </>
                );
              }}
            </SubmittableForm>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

function isValidIPv4(ip: string) {
  const ipv4Regex =
    /^((?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;
  return ipv4Regex.test(ip);
}

function isValidIPv4Cidr(cidr: string) {
  const ipv4CidrRegex =
    /^((?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\/([0-9]|[1,2][0-9]|3[0-2])$/;
  return ipv4CidrRegex.test(cidr);
}
